/**
 * AddWhenBranchMenu — replaces MergeRenderer's disabled `[+ Add WHEN]` footer
 * scaffold with a real shadcn DropdownMenu once editing is ready. Offers
 * dialect-aware, schema-driven WHEN branch templates (§2.5 decisions 3+5):
 * non-Oracle dialects always offer all three (matched→UPDATE, matched→DELETE,
 * not-matched→INSERT), auto-adding an `AND 1 = 1` placeholder condition when a
 * same-matchKind branch already exists (MSSQL/PG require a search condition to
 * disambiguate two WHEN clauses of the same kind); Oracle offers ONLY the
 * missing branch kind (max one matched + one not-matched) and disables the
 * button outright once both exist.
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — MERGE branch ops (§2.5 S7).
 */
import { Plus } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../../components/ui/dropdown-menu';
import type { ParseOptions } from '../../../../sql-grammar/model';
import { toCommitCtx, type VqeEditContext } from '../../edit/editContext';
import {
  addMergeBranch,
  computeAddWhenBranchItems,
  type AddWhenBranchKind,
} from '../../edit/edits/mergeBranchOps';
import { vqeCopy } from '../../ux/vqeCopy';
import { AddButton } from '../common/AddButton';
import { recordEditFailure, safeExtractStructure } from '../insert/insertColumnMapHelpers';
import {
  buildMatchedDeleteBranchText,
  buildMatchedUpdateBranchText,
  buildNotMatchedInsertBranchText,
  extractTemplateInputs,
} from './mergeBranchTemplates';

const MENU_LABELS: Record<AddWhenBranchKind, string> = {
  'matched-update': vqeCopy.actions.whenMatchedUpdate,
  'matched-delete': vqeCopy.actions.whenMatchedDelete,
  'not-matched-insert': vqeCopy.actions.whenNotMatchedInsert,
};

export interface AddWhenBranchMenuProps {
  readonly editContext?: VqeEditContext;
  readonly options: ParseOptions;
  /** Doc-order counts, EXCLUDING `not-matched-by-source` (see `mergeBranchTemplates.countMatchedNotMatchedBranches`). */
  readonly matchedCount: number;
  readonly notMatchedCount: number;
}

export function AddWhenBranchMenu({
  editContext,
  options,
  matchedCount,
  notMatchedCount,
}: AddWhenBranchMenuProps) {
  const items = computeAddWhenBranchItems(options.engine, matchedCount, notMatchedCount);

  if (!editContext) {
    return <AddButton label={vqeCopy.actions.addWhenBranch} testId="vqe-merge-add-branch" />;
  }
  // Oracle-full is structural (independent of readiness) — check it first so
  // the tooltip never claims "connect and wait" for a button that stays
  // disabled once ready anyway.
  if (options.engine === 'oracle' && items.length === 0) {
    return (
      <AddButton
        label={vqeCopy.actions.addWhenBranch}
        testId="vqe-merge-add-branch"
        tooltip={vqeCopy.disabled.mergeAddBranchOracleFull}
      />
    );
  }
  if (!editContext.service.getSchemaSnapshot()) {
    return (
      <AddButton
        label={vqeCopy.actions.addWhenBranch}
        testId="vqe-merge-add-branch"
        tooltip={vqeCopy.disabled.mergeAddBranchNotReady}
      />
    );
  }

  const onSelect = (kind: AddWhenBranchKind): void => {
    const structure = safeExtractStructure(editContext);
    const snapshot = editContext.service.getSchemaSnapshot();
    const inputs = structure && snapshot ? extractTemplateInputs(structure, snapshot) : null;
    if (!inputs) {
      recordEditFailure(
        new Error('addMergeBranch: no target columns available to build a WHEN branch'),
      );
      return;
    }
    const text =
      kind === 'matched-update'
        ? buildMatchedUpdateBranchText(inputs, matchedCount > 0)
        : kind === 'matched-delete'
          ? buildMatchedDeleteBranchText(matchedCount > 0)
          : buildNotMatchedInsertBranchText(inputs, notMatchedCount > 0);
    if (!text) {
      recordEditFailure(new Error('addMergeBranch: could not build a WHEN branch template'));
      return;
    }
    addMergeBranch(text, toCommitCtx(editContext)).catch(recordEditFailure);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs text-muted-foreground"
          data-testid="vqe-merge-add-branch"
        >
          <Plus className="h-3.5 w-3.5" />
          {vqeCopy.actions.addWhenBranch}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" data-testid="vqe-merge-add-branch-menu">
        {items.map((kind) => (
          <DropdownMenuItem
            key={kind}
            data-testid={`vqe-merge-add-branch-${kind}`}
            onSelect={() => onSelect(kind)}
          >
            {MENU_LABELS[kind]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
